import { User } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export function useUser() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setUser(session.user);
        // Store full session object
        localStorage.setItem('sb-session', JSON.stringify(session));
      } else {
        setUser(null);
        localStorage.removeItem('sb-session');
      }
      setIsLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setUser(session.user);
        localStorage.setItem('sb-session', JSON.stringify(session));
      } else {
        setUser(null);
        localStorage.removeItem('sb-session');
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async ({ username, password }: { username: string; password: string }) => {
    try {
      if (!username.includes('@')) {
        return { ok: false, message: 'Please enter a valid email address' };
      }
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: username,
        password,
      });

      if (error) {
        console.error('Auth error:', error);
        return { 
          ok: false, 
          message: error.message || 'Failed to sign in' 
        };
      }

      if (!data.user || !data.session) {
        return { ok: false, message: 'No user data returned' };
      }

      // Store the full session
      localStorage.setItem('sb-session', JSON.stringify(data.session));
      
      return { ok: true };
    } catch (error: any) {
      console.error('Login error:', error);
      return { 
        ok: false, 
        message: error.message || 'An unexpected error occurred' 
      };
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      localStorage.removeItem('sb-session');
      return { ok: true };
    } catch (error: any) {
      console.error('Logout error:', error);
      return { ok: false, message: error.message };
    }
  };

  const register = async ({ username, password }: { username: string; password: string }) => {
    try {
      if (!username.includes('@')) {
        return { ok: false, message: 'Please enter a valid email address' };
      }

      const { data, error } = await supabase.auth.signUp({
        email: username,
        password,
      });

      if (error) {
        console.error('Register error:', error);
        return { ok: false, message: error.message };
      }

      if (!data.user) {
        return { ok: false, message: 'No user data returned' };
      }

      // Store the session if available after registration
      if (data.session) {
        localStorage.setItem('sb-session', JSON.stringify(data.session));
      }

      return { ok: true };
    } catch (error: any) {
      console.error('Register error:', error);
      return { ok: false, message: error.message };
    }
  };

  return {
    user,
    isLoading,
    error,
    login,
    logout,
    register,
  };
}
